import { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Pin, PinOff, Search, Edit, Trash2, ChevronsLeft, X
} from 'lucide-react';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';

export interface BoardLite {
  id: string;
  name: string;
  color?: string;
  task_count?: number;
  is_default?: boolean;
  board_type?: string;
  workgroup_name?: string;
}

interface Props {
  boards: BoardLite[];
  selectedBoardId: string;
  pinned: boolean;
  onPinChange: (pinned: boolean) => void;
  onSelectBoard: (id: string) => void;
  onCreateBoard: () => void;
  onEditBoard: (board: BoardLite) => void;
  onDeleteBoard: (boardId: string) => void;
}

const RAIL_W = 56;
const EXPANDED_W = 264;

function badgeForType(type?: string, workgroupName?: string) {
  if (type === 'personal') return { label: 'Personale', cls: 'bg-purple-500/15 text-purple-700 dark:text-purple-300' };
  if (type === 'team') return { label: workgroupName || 'Team', cls: 'bg-green-500/15 text-green-700 dark:text-green-300' };
  if (type === 'project') return { label: 'Project', cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' };
  return null;
}

export function BoardsAuroraSidebar({
  boards, selectedBoardId, pinned, onPinChange,
  onSelectBoard, onCreateBoard, onEditBoard, onDeleteBoard,
}: Props) {
  const { t } = useAuroraTheme();
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [search, setSearch] = useState('');
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const expanded = pinned || hovered || focusWithin;

  useEffect(() => {
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, []);

  const filteredBoards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return boards;
    return boards.filter(b => b.name.toLowerCase().includes(q));
  }, [boards, search]);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHovered(true);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHovered(false), 120);
  };

  return (
    <>
      <div
        aria-label="Sidebar Board"
        className="hidden md:block relative shrink-0 h-full"
        style={{ width: pinned ? EXPANDED_W : RAIL_W }}
      >
        <motion.aside
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onFocus={() => setFocusWithin(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocusWithin(false);
          }}
          initial={false}
          animate={{ width: expanded ? EXPANDED_W : RAIL_W }}
          transition={{ duration: 0.28, ease: aurora.ease }}
          className="absolute inset-y-0 left-0 z-30 overflow-hidden flex flex-col"
          style={{
            background: t.panel,
            borderRight: `1px solid ${t.border}`,
            boxShadow: !pinned && expanded ? `8px 0 28px -12px ${t.bg}` : 'none',
            backdropFilter: 'blur(14px)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 pt-3 pb-2 shrink-0" style={{ minHeight: 48 }}>
            <AnimatePresence mode="wait" initial={false}>
              {expanded ? (
                <motion.span
                  key="title"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.18 }}
                  className="text-[12px] font-semibold uppercase tracking-[0.12em]"
                  style={{ color: t.muted }}
                >
                  Boards
                </motion.span>
              ) : (
                <motion.span
                  key="dot"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="w-2 h-2 rounded-full"
                  style={{ background: t.primary, boxShadow: `0 0 0 3px ${t.primary}22` }}
                />
              )}
            </AnimatePresence>

            <div className="flex items-center gap-1">
              <AnimatePresence>
                {expanded && (
                  <motion.button
                    key="newboard"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.18 }}
                    onClick={onCreateBoard}
                    aria-label="Nuova board"
                    className="w-7 h-7 rounded-md grid place-items-center transition-colors"
                    style={{ color: t.muted }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = t.panel2; e.currentTarget.style.color = t.text; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
                  >
                    <Plus className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
              <button
                onClick={() => onPinChange(!pinned)}
                aria-label={pinned ? 'Sblocca sidebar' : 'Fissa sidebar'}
                title={pinned ? 'Sblocca sidebar' : 'Fissa sidebar'}
                className="w-7 h-7 rounded-md grid place-items-center transition-colors"
                style={{ color: pinned ? t.primary : t.muted, background: pinned ? `${t.primary}18` : 'transparent' }}
                onMouseEnter={(e) => { if (!pinned) { e.currentTarget.style.background = t.panel2; e.currentTarget.style.color = t.text; } }}
                onMouseLeave={(e) => {
                  if (!pinned) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }
                }}
              >
                {pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Search (only when expanded and many boards) */}
          {expanded && boards.length > 6 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="px-3 pb-2 shrink-0"
            >
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: t.faint }} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filtra board…"
                  aria-label="Filtra board"
                  className="w-full h-8 pl-8 pr-2 rounded-md text-[12.5px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500"
                  style={{
                    background: t.panel2,
                    border: `1px solid ${t.border}`,
                    color: t.text,
                  }}
                />
              </div>
            </motion.div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
            {filteredBoards.map((board) => {
              const isSelected = selectedBoardId === board.id;
              const badge = badgeForType(board.board_type, board.workgroup_name);
              const color = board.color || t.primary;
              return (
                <div
                  key={board.id}
                  className="group/board relative"
                >
                  <button
                    onClick={() => onSelectBoard(board.id)}
                    aria-current={isSelected ? 'page' : undefined}
                    aria-label={`Apri board ${board.name}`}
                    className="w-full flex items-center gap-2.5 rounded-lg transition-colors text-left"
                    style={{
                      padding: expanded ? '7px 10px' : '8px 0',
                      justifyContent: expanded ? 'flex-start' : 'center',
                      background: isSelected ? `${color}1F` : 'transparent',
                      color: isSelected ? t.text : t.muted,
                      boxShadow: isSelected ? `inset 2px 0 0 0 ${color}` : 'none',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = t.panel2; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    title={!expanded ? `${board.name}${board.task_count ? ` · ${board.task_count}` : ''}` : undefined}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{
                        background: color,
                        boxShadow: isSelected ? `0 0 0 3px ${color}33` : 'none',
                      }}
                    />
                    {expanded && (
                      <>
                        <span className="flex-1 truncate text-[13px] font-medium" style={{ color: t.text }}>
                          {board.name}
                        </span>
                        {badge && (
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded font-medium max-w-[72px] truncate group-hover/board:hidden ${badge.cls}`}
                            title={badge.label}
                          >
                            {badge.label}
                          </span>
                        )}
                        <span
                          className="text-[10px] tabular-nums shrink-0 group-hover/board:hidden"
                          style={{ color: t.faint }}
                        >
                          {board.task_count || 0}
                        </span>
                      </>
                    )}
                  </button>

                  {expanded && (
                    <div className="absolute right-1.5 top-1/2 -translate-y-1/2 hidden group-hover/board:flex items-center gap-0.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); onEditBoard(board); }}
                        className="w-6 h-6 grid place-items-center rounded transition-colors"
                        style={{ color: t.muted }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = t.panel; e.currentTarget.style.color = t.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
                        aria-label="Modifica board"
                        title="Modifica board"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      {!board.is_default && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteBoard(board.id); }}
                          className="w-6 h-6 grid place-items-center rounded transition-colors"
                          style={{ color: t.muted }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = `${t.warning}15`; e.currentTarget.style.color = '#ef4444'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
                          aria-label="Elimina board"
                          title="Elimina board"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {!expanded && (
              <button
                onClick={onCreateBoard}
                aria-label="Nuova board"
                title="Nuova board"
                className="w-full mt-1 grid place-items-center rounded-md transition-colors"
                style={{ height: 32, color: t.muted }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.panel2; e.currentTarget.style.color = t.primary; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.aside>
      </div>
    </>
  );
}

interface MobileProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boards: BoardLite[];
  selectedBoardId: string;
  onSelectBoard: (id: string) => void;
  onCreateBoard: () => void;
  onEditBoard: (board: BoardLite) => void;
  onDeleteBoard: (boardId: string) => void;
}

export function BoardsAuroraDrawer({
  open, onOpenChange, boards, selectedBoardId,
  onSelectBoard, onCreateBoard, onEditBoard, onDeleteBoard,
}: MobileProps) {
  const { t } = useAuroraTheme();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const filteredBoards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return boards;
    return boards.filter(b => b.name.toLowerCase().includes(q));
  }, [boards, search]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
            onClick={() => onOpenChange(false)}
            aria-hidden
          />
          <motion.aside
            role="dialog"
            aria-label="Selettore board"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="md:hidden fixed inset-y-0 left-0 z-50 flex flex-col"
            style={{
              width: 280,
              background: t.panel,
              borderRight: `1px solid ${t.border}`,
              color: t.text,
            }}
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${t.border}` }}>
              <span className="text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: t.muted }}>Boards</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { onCreateBoard(); onOpenChange(false); }}
                  aria-label="Nuova board"
                  className="w-7 h-7 grid place-items-center rounded-md"
                  style={{ color: t.muted }}
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onOpenChange(false)}
                  aria-label="Chiudi"
                  className="w-7 h-7 grid place-items-center rounded-md"
                  style={{ color: t.muted }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {boards.length > 6 && (
              <div className="px-3 py-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: t.faint }} />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filtra board…"
                    className="w-full h-9 pl-8 pr-2 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500"
                    style={{ background: t.panel2, border: `1px solid ${t.border}`, color: t.text }}
                  />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
              {filteredBoards.map((board) => {
                const isSelected = selectedBoardId === board.id;
                const badge = badgeForType(board.board_type, board.workgroup_name);
                const color = board.color || t.primary;
                return (
                  <div key={board.id} className="group flex items-center">
                    <button
                      onClick={() => { onSelectBoard(board.id); onOpenChange(false); }}
                      className="flex-1 flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors"
                      style={{
                        background: isSelected ? `${color}1F` : 'transparent',
                        boxShadow: isSelected ? `inset 2px 0 0 0 ${color}` : 'none',
                      }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color, boxShadow: isSelected ? `0 0 0 3px ${color}33` : 'none' }} />
                      <span className="flex-1 truncate text-[13px] font-medium" style={{ color: t.text }}>{board.name}</span>
                      {badge && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium max-w-[80px] truncate ${badge.cls}`}>
                          {badge.label}
                        </span>
                      )}
                      <span className="text-[10px] tabular-nums" style={{ color: t.faint }}>{board.task_count || 0}</span>
                    </button>
                    <div className="flex items-center pr-2 shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); onEditBoard(board); }}
                        className="w-9 h-9 grid place-items-center rounded-md"
                        style={{ color: t.muted }}
                        aria-label={`Modifica board ${board.name}`}
                        title="Modifica"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      {!board.is_default && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteBoard(board.id); }}
                          className="w-9 h-9 grid place-items-center rounded-md"
                          style={{ color: '#ef4444' }}
                          aria-label={`Elimina board ${board.name}`}
                          title="Elimina"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-3" style={{ borderTop: `1px solid ${t.border}` }}>
              <button
                onClick={() => onOpenChange(false)}
                className="w-full h-9 grid place-items-center rounded-md text-[12.5px] font-medium gap-1.5 inline-flex"
                style={{ background: t.panel2, color: t.muted, border: `1px solid ${t.border}` }}
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
                Chiudi
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export default BoardsAuroraSidebar;
